/**
 * Admin error boundary — props aligned with monorepo
 * `AdminErrorBoundaryContract` (see MFE-v3-G-EB-ADMIN / ERROR_BOUNDARY_CONTRACT.md).
 *
 * Staff/customer/kiosk/pickup apps use `ErrorBoundaryContract` (`fallback?: ReactNode` + `retryKey`).
 * Admin uses a function fallback so section UI can read the caught error and call `resetError`.
 */
import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';
import i18n from '../../i18n/i18n.js';
import { captureBoundaryError } from 'pi-kiosk-shared/sentry';
import { toAdminFacingErrorMessage } from '../../errors/toAdminFacingErrorMessage.js';
import { appLogger } from '../../logging/appLogger.js';

export type ErrorBoundaryFallbackRender = (
  error: Error,
  resetError: () => void,
) => ReactNode;

/** Monorepo contract for rpapp-admin error boundaries. */
export interface AdminErrorBoundaryContract {
  readonly children: ReactNode;
  readonly fallback?: ErrorBoundaryFallbackRender;
}

export interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export type ErrorBoundaryProps = AdminErrorBoundaryContract;

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ error, errorInfo });
    appLogger.error('ErrorBoundary caught an error', error, {
      module: 'ErrorBoundary',
      feature: 'shell',
      operation: 'componentDidCatch',
      componentStack: errorInfo.componentStack,
    });
    captureBoundaryError(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (this.state.hasError && prevProps.children !== this.props.children) {
      this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    }
  }

  private resetError = (): void => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.resetError);
      }

      const safeMessage = toAdminFacingErrorMessage(this.state.error);
      return (
        <div className="error-boundary">
          <h2>{i18n.t('shared.errorBoundary.title')}</h2>
          <p>{safeMessage}</p>
          <details>
            <summary>{i18n.t('shared.errorBoundary.detailsSummary')}</summary>
            <p>{safeMessage}</p>
          </details>
          <button type="button" onClick={this.resetError}>
            {i18n.t('shared.errorBoundary.retry')}
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
